import React, { useState, useEffect, useRef } from "react"
import { sendPasswordResetEmail } from "firebase/auth"
import { TextField, Button, Container, Typography, Alert, Paper, Stack, CircularProgress } from "@mui/material"
import { auth } from "@/config/firebase"

const COOLDOWN_SECONDS = 60
const MAX_ATTEMPTS = 5

const ForgotPassword = () => {
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [attempts, setAttempts] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS)
    timerRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current)
          timerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleReset = async () => {
    if (cooldown > 0 || attempts >= MAX_ATTEMPTS) return
    setMessage("")
    setError("")
    setLoading(true)
    try {
      await sendPasswordResetEmail(auth, email)
      setMessage("Password reset email sent!")
      setAttempts(prev => prev + 1)
      startCooldown()
    } catch (err: unknown) {
      const code = (err as any)?.code as string | undefined
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        setError("No account found with that email address.")
      } else if (code === "auth/too-many-requests") {
        setError("Too many requests. Please wait a moment before trying again.")
      } else {
        setError("Failed to send reset email. Please check your address and try again.")
      }
    }
    setLoading(false)
  }

  const attemptsExhausted = attempts >= MAX_ATTEMPTS
  const isDisabled = !email || loading || cooldown > 0 || attemptsExhausted

  return (
    <Container maxWidth="sm" style={{ display: "flex", alignItems: "center", height: "100vh" }}>
      <Paper variant="outlined" style={{ padding: "20px", width: "100%" }}>
        <Stack gap={1}>
          <Typography variant="h5" gutterBottom>
            Forgot Password
          </Typography>
          {message && <Alert severity="success">{message}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
          {attemptsExhausted && (
            <Alert severity="warning">Too many reset attempts. Please try again later or contact support.</Alert>
          )}
          <TextField
            label="Enter your email"
            variant="outlined"
            size="small"
            fullWidth
            margin="normal"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={loading || cooldown > 0 || attemptsExhausted}
          />
          <Button
            size="small"
            variant="contained"
            color="primary"
            fullWidth
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            disabled={isDisabled}
            onClick={handleReset}>
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Reset Password"}
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}

export default ForgotPassword
